import { useContext, useEffect, type ReactNode } from "react";
import { NavLink, useLocation, useNavigate } from "react-router";
import { ChevronDown } from "lucide-react";
import { paths } from "@shared-core/routing/routes";
import {
  useSupplierPortalAdapter,
  useSupplierPortalSignIn,
} from "@shared-core/data/SupplierPortalAdapterContext";
import { useLoad } from "@shared-core/domain/useLoad";
import { mutationErrorMessage } from "@shared-core/domain/mutation-error-message";
import {
  ACTION_WORDING,
  STAFF_VIEW_WORDING,
} from "@shared-core/domain/supplier-portal-wording";
import type {
  CompanySummary,
  SupplierSession,
} from "@shared-core/data/supplier-portal-adapter";
import { Button } from "../../app/components/primitives/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../app/components/primitives/dropdown-menu";
import { cn } from "../../app/components/primitives/utils";
import { CompanySummaryContext } from "./CompanySummaryContext";
import { SupplierAccountMenuContext } from "./SupplierAccountMenuContext";
import { SupplierHeader } from "./SupplierHeader";
import { SupplierSessionContext } from "./session";
import { useIsStaffView } from "./staffView";
import { LoadFailed, LoadingCard } from "./states";

/**
 * Chrome for a signed-in supplier: who they are, and the Orders / Quote requests / Picks / Invoices / Your company / Account nav
 * with what is waiting on them. Picks shows when the supplier runs a warehouse for us. Asks for the session once; nobody signed in
 * goes to sign in and comes back here.
 */
export function SignedInShell({ children }: { children: ReactNode }) {
  const signIn = useSupplierPortalSignIn();
  const portal = useSupplierPortalAdapter();
  const location = useLocation();
  const navigate = useNavigate();
  const isStaffView = useIsStaffView();
  const renderAccountMenu = useContext(SupplierAccountMenuContext);
  const {
    data: session,
    error,
    isLoading,
    reload,
  } = useLoad(() => signIn.getSession(), "session");
  // Your company's counts are a supplier-side read of their own, not part of the session.
  const company = useLoad(() => portal.getCompanySummary(), "company-summary");
  const returnTo = `${location.pathname}${location.search}`;

  useEffect(() => {
    if (!isLoading && !error && session === null) {
      navigate(paths.supplierSignIn({ returnTo }), { replace: true });
    }
  }, [isLoading, error, session, navigate, returnTo]);

  if (error && !session) {
    return (
      <Frame right={null}>
        <LoadFailed
          what="your account"
          message={mutationErrorMessage(
            error,
            "We couldn't check your sign-in.",
          )}
          onRetry={reload}
        />
      </Frame>
    );
  }
  if (!session) {
    return (
      <Frame right={null}>
        <LoadingCard what="your account" />
      </Frame>
    );
  }

  return (
    <SupplierSessionContext.Provider value={{ session, refresh: reload }}>
      <CompanySummaryContext.Provider
        value={{
          summary: company.data,
          isLoading: company.isLoading,
          refresh: company.reload,
        }}
      >
        <Frame
          right={
            renderAccountMenu ? (
              renderAccountMenu(session)
            ) : (
              <WhoMenu
                session={session}
                onSignOut={async () => {
                  await signIn.signOut();
                  // A staff view's signOut leaves for One by a full navigation: there is no sign-in page to go to.
                  if (!isStaffView) navigate(paths.supplierSignIn());
                }}
              />
            )
          }
          nav={
            <PortalNav
              session={session}
              company={company.data}
              hasCompany={!isStaffView}
            />
          }
        >
          {children}
        </Frame>
      </CompanySummaryContext.Provider>
    </SupplierSessionContext.Provider>
  );
}

function Frame({
  right,
  nav,
  children,
}: {
  right: ReactNode;
  nav?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="min-h-screen bg-muted/30">
      <SupplierHeader right={right} />
      {nav}
      <main className="mx-auto grid max-w-5xl gap-4 px-4 py-5 sm:px-6">
        {children}
      </main>
    </div>
  );
}

function WhoMenu({
  session,
  onSignOut,
}: {
  session: SupplierSession;
  onSignOut: () => void;
}) {
  const isStaffView = useIsStaffView();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="text-muted-foreground">
          <span className="hidden sm:inline">{session.supplier_name} · </span>
          {isStaffView ? STAFF_VIEW_WORDING.menuPerson : session.person_name}
          <ChevronDown aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem asChild>
          <NavLink to={paths.supplierAccount()}>Account</NavLink>
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={onSignOut}>
          {isStaffView ? STAFF_VIEW_WORDING.menuExit : ACTION_WORDING.signOut}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function PortalNav({
  session,
  company: companySummary,
  hasCompany,
}: {
  session: SupplierSession;
  /** What is waiting under Your company; null until it has loaded. */
  company: CompanySummary | null;
  /** Your company is the supplier's own: a staff view does not show it. */
  hasCompany: boolean;
}) {
  const orders = {
    to: paths.supplierOrders(),
    label: "Orders",
    count: session.waiting.orders_to_accept ?? 0,
    countLabel: "to accept",
  };
  const quoteRequests = {
    to: paths.supplierQuoteRequests(),
    label: "Quote requests",
    count: session.waiting.quote_requests_to_answer ?? 0,
    countLabel: "to answer",
  };
  const picks = {
    to: paths.supplierPicks(),
    label: "Picks",
    count: session.waiting.picks_to_pick,
    countLabel: "to pick",
  };
  const goodsIn = {
    to: paths.supplierGoodsIn(),
    label: "Goods in",
    count: session.waiting.goods_in_due,
    countLabel: "due in",
  };
  const invoices = {
    to: paths.supplierInvoices(),
    label: "Invoices",
    count: session.waiting.invoice_queries ?? 0,
    countLabel: "to answer",
  };
  const account = {
    to: paths.supplierAccount(),
    label: "Account",
    count: 0,
    countLabel: "",
  };
  // Your company (supplier diligence) leads while an assessment or a renewal is waiting, from its own read.
  const companyToDo = companySummary?.to_do ?? 0;
  const company = {
    to: paths.supplierCompany(),
    label: "Your company",
    count: companyToDo,
    countLabel: "to do",
  };
  const items = [
    ...(hasCompany && companyToDo > 0 ? [company] : []),
    orders,
    quoteRequests,
    ...(session.warehouses.length > 0 ? [picks, goodsIn] : []),
    invoices,
    ...(hasCompany && companyToDo === 0 ? [company] : []),
    account,
  ];
  return (
    <nav aria-label="Supplier portal" className="border-b bg-card">
      <div className="mx-auto flex max-w-5xl gap-1 overflow-x-auto px-4 sm:px-6">
        {items.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              cn(
                "border-b-2 px-2.5 py-2.5 text-sm whitespace-nowrap",
                isActive
                  ? "border-primary font-semibold text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground",
              )
            }
          >
            {item.label}
            {item.count > 0 && (
              <CountBadge
                n={item.count}
                tone="todo"
                label={`${item.count} ${item.countLabel}`}
              />
            )}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}

/** The small count beside a tab: pink when it is waiting on the supplier, grey when it is just a tally. */
export function CountBadge({
  n,
  tone,
  label,
}: {
  n: number;
  tone: "todo" | "mute";
  label: string;
}) {
  return (
    <span
      aria-label={label}
      className={cn(
        "ml-1.5 inline-block min-w-[1.25rem] rounded-full px-1.5 text-center text-[11px] font-semibold",
        tone === "todo"
          ? "bg-primary text-primary-foreground"
          : "bg-muted text-muted-foreground",
      )}
    >
      {n}
    </span>
  );
}
